import React, { useEffect, useState } from 'react'
import { CalendarCheck2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { getCalendarOptions, setMainCalendarProvider } from '../services/bookingsApi'
import type { CalendarChoice } from '../types/index.js'

type Props = { workspaceId: string }

const CHOICE_LABEL: Record<CalendarChoice, string> = { google: 'Google', outlook: 'Outlook', both: 'Both' }

// Only meaningful once both Google and Outlook calendars are connected — with
// just one connected there's no real choice to make, so this renders nothing.
const MainCalendarPreference: React.FC<Props> = ({ workspaceId }) => {
  const { currentTheme: t } = useTheme()
  const [bothConnected, setBothConnected] = useState(false)
  const [choice, setChoice] = useState<CalendarChoice | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    getCalendarOptions(workspaceId)
      .then((opts) => {
        if (cancelled) return
        setBothConnected(opts.googleConnected && opts.outlookConnected)
        setChoice(opts.savedDefault)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [workspaceId])

  if (!bothConnected) return null

  const handlePick = (next: CalendarChoice) => {
    setChoice(next)
    setSaving(true)
    setMainCalendarProvider(workspaceId, next).catch(() => {}).finally(() => setSaving(false))
  }

  return (
    <div className="rounded-xl p-4 flex flex-col gap-3" style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}>
      <div className="flex items-start gap-2.5">
        <CalendarCheck2 size={16} className="mt-0.5 shrink-0" style={{ color: t.mutedText }} />
        <div>
          <p className="text-sm font-semibold" style={{ color: t.text }}>Main calendar for booking</p>
          <p className="text-[12.5px] mt-0.5" style={{ color: t.mutedText }}>
            Both are connected — pick which one new meetings book to by default. You can override this on each booking.
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        {(['google', 'outlook', 'both'] as CalendarChoice[]).map((c) => (
          <button
            key={c}
            type="button"
            disabled={saving}
            onClick={() => handlePick(c)}
            className="flex-1 text-xs font-medium py-2 rounded-md border transition-colors disabled:opacity-50"
            style={{
              borderColor: choice === c ? t.accent : t.border,
              backgroundColor: choice === c ? t.hoverBackground : 'transparent',
              color: t.text,
            }}
          >
            {CHOICE_LABEL[c]}
          </button>
        ))}
      </div>
    </div>
  )
}

export default React.memo(MainCalendarPreference)
